import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Input } from '@dreamtree-org/twreact-ui';
import { useMutation } from '@/lib/useQuery';
import { useAppApi } from '@/lib/api';
import { errorText } from '@/lib/format';
import { isValidPhone } from '../lib/cart';
import { sendFirebaseCode } from '../lib/firebasePhone';

const RECAPTCHA_ID = 'mp-recaptcha';
const national = (phone) => phone.replace(/\D/g, '').slice(-10);

/** How this store signs shoppers in (Customer.signInMethod); null while loading, `code` on failure. */
export function useSignInMethod() {
  const { fetchApi } = useAppApi();
  // Load once: fetchApi changes identity every render.
  const api = useRef(fetchApi);
  const [method, setMethod] = useState(null);
  useEffect(() => {
    let live = true;
    api.current('sfSignInMethod', {}, false)
      .then((r) => { if (live) setMethod(r.data); })
      .catch(() => { if (live) setMethod({ method: 'code', demo: false }); });
    return () => { live = false; };
  }, []);
  return method;
}

/** Server error codes that end a code: the shopper must request a new one (resend unlocks at once). */
const CODE_DEAD = new Set(['OTP_EXPIRED', 'OTP_LOCKED']);

/** { code, status, message } of a failed request (the crud door answers { error, code }). */
export function otpFailure(ex) {
  const res = ex && ex.response;
  return { code: (res && res.data && res.data.code) || null, status: (res && res.status) || null, message: errorText(ex) };
}

/** Title of the alert for a failure; the server's sentence is the body. */
export function otpFailureTitle(f) {
  if (!f) return null;
  if (f.code === 'RATE_LIMITED' || (f.status === 429 && f.code !== 'OTP_LOCKED')) return 'Too many codes requested';
  if (f.code === 'OTP_EXPIRED') return 'Code expired';
  if (f.code === 'OTP_LOCKED') return 'Too many wrong attempts';
  if (f.code === 'OTP_INVALID') return 'Wrong code';
  return null;
}

/**
 * Phone + one-time code sign-in form (WEB-06, C-05), shared by the sign-in page
 * and the inline checkout step (MERCH-56). Runs Customer.requestOtp / verifyOtp
 * (or Firebase when the store's sign-in method is `firebase`, D30) and hands the
 * new session to `onVerified(session)`. The server merges the guest cart at
 * sign-in. Marketing consent (WhatsApp) is a separate, unticked, optional control.
 *
 * Props: method ({ method: 'code'|'firebase', firebase? } from Customer.signInMethod;
 * null while loading), onVerified(session), submitLabel.
 */
export default function PhoneOtpForm({ method, onVerified, submitLabel = 'Verify and continue' }) {
  const send = useMutation('sfOtpSend');
  const verify = useMutation('sfOtpVerify');
  const verifyFirebase = useMutation('sfOtpVerifyFirebase');
  const [firebaseBusy, setFirebaseBusy] = useState(false);
  const confirmRef = useRef(null);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [request, setRequest] = useState(null);
  const [terms, setTerms] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const [wait, setWait] = useState(0);
  const [failure, setFailure] = useState(null);
  const useFirebase = method && method.method === 'firebase';

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const fail = (message) => setFailure({ code: null, status: null, message });

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    setFailure(null);
    if (!isValidPhone(phone)) return fail('Enter a valid 10-digit Indian mobile number.');
    if (!terms) return fail('Please accept the terms and privacy policy to continue.');
    if (useFirebase) {
      setFirebaseBusy(true);
      try {
        const n = national(phone);
        confirmRef.current = await sendFirebaseCode(method.firebase, `+91${n}`, RECAPTCHA_ID);
        setRequest({ firebase: true, sent_to: `+91 ${n.slice(0, 2)}******${n.slice(-2)}`, resend_after_s: 30, expires_in_s: 300 });
        setWait(30);
      } catch (ex) {
        fail(ex.message);
      } finally {
        setFirebaseBusy(false);
      }
      return undefined;
    }
    try {
      const r = await send.run({ phone });
      setRequest(r.data);
      setCode('');
      setWait(r.data.resend_after_s);
    } catch (ex) {
      setFailure(otpFailure(ex));
    }
    return undefined;
  };

  const verifyCode = async (e) => {
    if (e) e.preventDefault();
    setFailure(null);
    try {
      let r;
      if (request && request.firebase) {
        setFirebaseBusy(true);
        let idToken;
        try {
          idToken = await confirmRef.current.confirm(code);
        } catch (ex) {
          return fail(ex.message);
        } finally {
          setFirebaseBusy(false);
        }
        r = await verifyFirebase.run({ id_token: idToken, whatsapp_updates: whatsapp });
      } else {
        r = await verify.run({ phone, code, request_id: request && request.request_id, whatsapp_updates: whatsapp });
      }
      await onVerified(r.data);
    } catch (ex) {
      const f = otpFailure(ex);
      setFailure(f);
      if (CODE_DEAD.has(f.code)) setWait(0);
    }
    return undefined;
  };

  const title = otpFailureTitle(failure);

  return (
    <div className="space-y-4">
      {!request ? (
        <form onSubmit={sendCode} className="space-y-4" noValidate>
          <Input label="Mobile number" type="tel" inputMode="numeric" autoComplete="tel" placeholder="98765 43210" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d ]/g, ''))} helperText="We'll send a one-time code to this number." required />
          <Checkbox checked={terms} onChange={(e) => setTerms(e.target.checked)} required label="I agree to the Terms of Use and Privacy Policy." />
          <Checkbox checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} label="Optional: send me order and repair updates on WhatsApp. You can still check out without this." />
          <Button type="submit" fullWidth size="lg" loading={send.loading || firebaseBusy} disabled={!method}>Send code</Button>
        </form>
      ) : (
        <form onSubmit={verifyCode} className="space-y-4" noValidate>
          <p className="text-sm">Code sent to <strong>{request.sent_to}</strong>. It expires in {Math.round(request.expires_in_s / 60)} minutes.</p>
          {request.dev_code ? (
            <Alert variant="info" title="Demo environment">
              No SMS is sent here. Your code is <strong className="font-mono">{request.dev_code}</strong>{' '}
              <button type="button" className="underline" onClick={() => setCode(request.dev_code)}>Use it</button>
            </Alert>
          ) : null}
          <Input label="6-digit code" type="tel" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
          <Button type="submit" fullWidth size="lg" loading={verify.loading || verifyFirebase.loading || firebaseBusy} disabled={code.length !== 6}>{submitLabel}</Button>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <Button variant="ghost" size="sm" onClick={() => { setRequest(null); setCode(''); setFailure(null); confirmRef.current = null; }}>Change number</Button>
            <Button variant="ghost" size="sm" disabled={wait > 0} loading={send.loading} onClick={() => sendCode()}>{wait > 0 ? `Resend in ${wait}s` : 'Resend code'}</Button>
          </div>
          {request.attempts_left === 0 ? <p className="text-xs opacity-70">This was the last code you can request for 10 minutes.</p> : null}
        </form>
      )}

      {failure ? <Alert variant={title === 'Too many codes requested' ? 'warning' : 'error'} title={title || undefined}>{failure.message}</Alert> : null}
      <div id={RECAPTCHA_ID} />
    </div>
  );
}
